import { Box, Button, Divider, Flex, Text } from '@chakra-ui/react';
import { Keyboard } from 'modules/common/components/Keyboard';
import { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CheckContextMenuItemFnArgs,
  ContextMenuItem as ContextMenuItemI,
  OnContextMenuItemClickFn,
} from './ContextMenu.types';

interface ContextMenuItemProps {
  menuItemData: ContextMenuItemI;
  contextMenuData?: CheckContextMenuItemFnArgs;
  onClickWrapper?: (e: MouseEvent<HTMLButtonElement>, callback: (args: OnContextMenuItemClickFn) => void) => void;
}

export const ContextMenuItem = ({ menuItemData, onClickWrapper, contextMenuData }: ContextMenuItemProps) => {
  const { label, onClick, id, checkIsDisabled, checkIsHidden, shortcuts, icon, hasDivider } = menuItemData;
  const { t } = useTranslation('common_navbar', { useSuspense: false, keyPrefix: 'widgetActions' });
  const isDisabled = checkIsDisabled && checkIsDisabled(contextMenuData || {});

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (onClickWrapper) {
        onClickWrapper(e, onClick);
      } else {
        onClick({
          e,
          activeWidgetIds: contextMenuData?.activeWidgetIds,
        });
      }
    },
    [onClickWrapper, onClick, contextMenuData],
  );

  if (checkIsHidden && checkIsHidden(contextMenuData || {})) {
    return null;
  }

  return (
    <>
      {hasDivider && <Divider />}
      <Button
        w='full'
        variant='ghost'
        key={id}
        size='sm'
        py={5}
        gap={4}
        fontSize='xs'
        alignItems='center'
        onClick={handleClick}
        justifyContent='space-between'
        disabled={isDisabled}
        borderRadius={0}
        data-testid={`context-menu-item-${id}`}
        color='font.500'
      >
        <Flex justifyContent='flex-start' gap={2} alignItems='center'>
          {icon}
          <Text fontSize='xs' fontWeight='normal'>
            {t(label as keyof typeof t)}
          </Text>
        </Flex>
        {shortcuts && (
          <Box>
            <Keyboard color='font.300' fontSize='0.625rem' macOs={shortcuts.macOs} shortcut={shortcuts.others} />
          </Box>
        )}
      </Button>
    </>
  );
};
